
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Play, Eye, Globe, ImageOff } from 'lucide-react';

interface PortfolioItemProps {
  title: string;
  description: string;
  thumbnail: string;
  type: 'photo' | 'video' | 'website';
  onClick?: () => void;
}

export const PortfolioItem = ({
  title,
  description,
  thumbnail,
  type,
  onClick,
}: PortfolioItemProps) => {
  // Reliable fallback images that are guaranteed to work
  const fallbackImages = {
    video: "https://images.unsplash.com/photo-1482938289607-e9573fc25ebb?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    photo: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    website: "https://images.unsplash.com/photo-1426604966848-d7adac402bff?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
  };
  
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  
  // Preload the image
  useEffect(() => {
    const img = new Image();
    img.src = thumbnail;
    img.onload = () => {
      setImageSrc(thumbnail);
    };
    img.onerror = () => {
      console.error(`Failed to load image: ${thumbnail}`);
      setImageError(true);
      // Try to load the fallback immediately
      const fallbackImg = new Image();
      fallbackImg.src = fallbackImages[type];
      fallbackImg.onload = () => {
        setImageSrc(fallbackImages[type]);
      };
      fallbackImg.onerror = () => {
        console.error('Even fallback image failed to load');
        setImageSrc(null);
      };
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [thumbnail, type]);
  
  return (
    <div
      onClick={onClick}
      className={cn(
        'group relative cursor-pointer overflow-hidden rounded-lg shadow-md',
        'transition-all duration-300 hover:shadow-xl h-64'
      )}
    >
      <div className="w-full h-full">
        {!imageSrc ? (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <ImageOff className="h-8 w-8 text-gray-500" />
          </div>
        ) : (
          <img
            src={imageSrc}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}
      </div>
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            {type === 'video' ? (
              <Play className="h-6 w-6 text-white fill-white" />
            ) : type === 'website' ? (
              <Globe className="h-6 w-6 text-white" />
            ) : (
              <Eye className="h-6 w-6 text-white" />
            )}
          </div>
        </div>
        <h3 className="text-white font-medium text-lg">{title}</h3>
        <p className="text-white/80 text-sm">{description}</p>
      </div>
    </div>
  );
};
